/**
 * MMO State Manager
 * Manages persistent world state, fog of war, and entity tracking for MMO mode
 */

import type { HexCoord } from '../shared/HexUtils';
import type { OrbitalPlatformData } from '../entities/mmo/MMOOrbitalPlatform.js';

// MMO Entity Interfaces
export interface MMOUnit {
  id: string;
  type: string;
  shipVariant?: string | null;
  ownerId: string;
  position: HexCoord;
  currentHp: number;
  maxHp: number;
  shields: number;
  maxShields: number;
  movementOrder: { q: number; r: number; turnsRemaining: number } | null;
  speed: number;
  attackRange: number;
  attack: number;
  defense: number;
  dockedAtPlanetId?: string | null;
  cargoCapacity?: number;
  cargoUsed?: number;
  miningAsteroidId?: string | null;
  miningProgress?: number;
  colorTint?: number | null;
  customName?: string | null;
  ownerName?: string | null;
  activeEffects?: Array<{ effectType: string; remainingTicks: number }>; // Active status effects
  abilities?: Array<{
    abilityTypeId: number;
    name: string;
    effectType: string;
    cooldownTicks: number;
    durationTicks: number;
    currentCooldown: number;
    description: string;
  }>;
}

export interface MMOPlanet {
  id: string;
  name: string;
  ownerId: string | null;
  position: HexCoord;
  currentHp: number;
  maxHp: number;
  shields: number;
  maxShields: number;
  buildings: any[];
  productionQueue: any[];
  isHomePlanet: boolean;
  hasSpaceStation?: boolean;
  maxStorage?: number;
}

export interface MMOAsteroid {
  id: string;
  ownerId: string | null;
  position: HexCoord;
  miningLevel: number;
  storedResources: number;
  maxStorage: number;
  resourceRichness: number;
  asteroidType?: 'regular' | 'golden' | 'welcome' | 'special' | 'crypto'; // Type of asteroid
  variant?: 'common_gold' | 'rare_platinum' | 'legendary_dark_matter' | 'welcome_gift'; // Asteroid variant
  rewardValue?: number | null;
  cryptoToken?: string | null;  // e.g. 'GALA', 'GUSDC'
  cryptoChain?: string | null;  // e.g. 'galachain', 'solana'
  mineralComposition?: Record<string, number> | null; // Mineral breakdown for regular asteroids
}

export interface MMOStationData {
  id: string;
  userId: string;
  stationType: string;
  position: HexCoord;
  currentHp: number;
  maxHp: number;
  storedAsteroids: number;
  maxStorage: number;
  stationFeePct: number;
  status: string;
  miningPlanetId?: string | null;
  relocationTarget?: { q: number; r: number };
  relocationPathLength?: number;
  relocationTicksPerHex?: number;
  relocationTickCounter?: number;
}

export interface MMOWreckData {
  id: string;
  ownerId: string;
  unitType: string;
  position: HexCoord;
  towStatus: string;
  repairStatus: string;
  destroyedAt: string;
  expiresAt: string;
}

export interface MMOAmbientEntityData {
  id: string;
  entityType: string;
  entitySubtype: string;
  position: HexCoord;
  velocity: HexCoord;
  speed: number;
  displayName: string;
  properties: Record<string, any>;
  lootable: boolean;
  scannable: boolean;
  scanData?: Record<string, any>;
  currentHp: number;
  maxHp: number;
}

export interface MMOLootDropData {
  id: string;
  position: HexCoord;
  sourceUnitType: string;
  hasComponent: boolean;
  hasMinerals: boolean;
  hasGold: boolean;
  topDealerId: string | null;
  exclusiveUntil: string;
  expiresAt: string;
}

export interface ChunkData {
  chunkX: number;
  chunkY: number;
  units: MMOUnit[];
  planets: MMOPlanet[];
  asteroids: MMOAsteroid[];
  stations?: MMOStationData[];
  wrecks?: MMOWreckData[];
  ambientEntities?: MMOAmbientEntityData[];
}

export interface AllianceData {
  id: number;
  name: string;
  tag: string;
  leaderId: string;
  memberCount: number;
}

export interface OfflineRecapEvent {
  type: 'damaged' | 'destroyed';
  unitType: string;
  unitId: string;
  position: { q: number; r: number };
  attackerName: string;
  totalDamageReceived: number;
  currentHp?: number;
  maxHp?: number;
  wreckId?: string;
  timestamp: string;
}

export interface TerritoryData {
  allianceId: number;
  name: string;
  hqQ: number;
  hqR: number;
  radius: number;
  color: string | null;
}

export interface MMOWorldStatePayload {
  worldId: number;
  fogOfWar: string[]; // Explored hexes as "q,r"
  currentVision: string[]; // Currently visible hexes as "q,r"
  chunks: ChunkData[];
  alliance?: AllianceData; // Player's alliance data (if in one)
  offlineRecap?: OfflineRecapEvent[];
  territories?: TerritoryData[];
}

export class MMOStateManager {
  private worldId: number = 1;
  private userId: string | null = null;
  private tickNumber: number = 0;

  // Fog of War
  private fogOfWar: Set<string> = new Set(); // Explored hexes "q,r"
  private currentVision: Set<string> = new Set(); // Currently visible "q,r"

  // Chunk tracking
  private chunks: Map<string, ChunkData> = new Map(); // "chunkX,chunkY" => ChunkData

  // Entity tracking (all entities from all loaded chunks)
  private units: Map<string, MMOUnit> = new Map(); // unitId => MMOUnit
  private planets: Map<string, MMOPlanet> = new Map(); // planetId => MMOPlanet
  private asteroids: Map<string, MMOAsteroid> = new Map(); // asteroidId => MMOAsteroid
  private stations: Map<string, MMOStationData> = new Map(); // stationId => MMOStationData
  private wrecks: Map<string, MMOWreckData> = new Map(); // wreckId => MMOWreckData
  private ambientEntities: Map<string, MMOAmbientEntityData> = new Map(); // entityId => MMOAmbientEntityData
  private lootDrops: Map<string, MMOLootDropData> = new Map(); // lootId => MMOLootDropData
  private platforms: Map<string, OrbitalPlatformData> = new Map(); // platformId => OrbitalPlatformData
  private territories: Map<number, TerritoryData> = new Map(); // allianceId => TerritoryData

  // Spatial index for fast hex lookups
  private entitiesAtHex: Map<string, string[]> = new Map(); // "q,r" => [entityIds]

  // Chunk-to-entity mapping for unloading
  private chunkEntities: Map<string, Set<string>> = new Map(); // "chunkX,chunkY" => Set<entityId>

  // Player data
  private playerAlliance: AllianceData | null = null;
  private playerResources: number = 0;

  // Build queue tracking
  private pendingBuilds: Map<string, {
    buildId: string;
    planetId: string;
    unitType: string;
    remainingSeconds: number;
    totalSeconds: number;
  }> = new Map();

  // Offline recap (populated once on world join, consumed by MMOGameScene)
  private pendingOfflineRecap: OfflineRecapEvent[] | null = null;

  /**
   * Initialize MMO state with user ID
   */
  public initialize(userId: string, worldId: number = 1): void {
    this.userId = userId;
    this.worldId = worldId;
    this.reset();
  }

  /**
   * Reset all state (for disconnects/reconnects)
   */
  public reset(): void {
    this.fogOfWar.clear();
    this.currentVision.clear();
    this.chunks.clear();
    this.units.clear();
    this.planets.clear();
    this.asteroids.clear();
    this.stations.clear();
    this.wrecks.clear();
    this.ambientEntities.clear();
    this.lootDrops.clear();
    this.platforms.clear();
    this.territories.clear();
    this.entitiesAtHex.clear();
    this.chunkEntities.clear();
    this.playerAlliance = null;
    this.pendingBuilds.clear();
    this.tickNumber = 0;
  }

  /**
   * Set complete world state from server
   */
  public setWorldState(state: MMOWorldStatePayload): void {
    console.log('[MMOState] Setting world state', state);

    this.worldId = state.worldId;

    // Update fog of war
    this.fogOfWar.clear();
    this.currentVision.clear();

    state.fogOfWar.forEach((hexKey) => this.fogOfWar.add(hexKey));
    state.currentVision.forEach((hexKey) => this.currentVision.add(hexKey));

    // Load all chunks
    state.chunks.forEach((chunk) => this.updateChunk(chunk));

    // Load alliance data if present
    if (state.alliance) {
      this.playerAlliance = state.alliance;
      console.log(`[MMOState] Alliance loaded: ${state.alliance.name} [${state.alliance.tag}]`);
    }

    console.log(
      `[MMOState] World state set: ${this.fogOfWar.size} explored, ${this.currentVision.size} visible, ${state.chunks.length} chunks`,
    );

    // Load territory data if present
    if (state.territories && state.territories.length > 0) {
      this.setTerritories(state.territories);
      console.log(`[MMOState] Loaded ${state.territories.length} alliance territories`);
    }

    // Store offline recap for the game scene to consume
    if (state.offlineRecap && state.offlineRecap.length > 0) {
      console.log(`[MMOState] Storing ${state.offlineRecap.length} offline recap events`);
      this.pendingOfflineRecap = state.offlineRecap;
    }

    // Check if player has any entities, spawn starters if not
    if (this.userId && this.getPlayerUnits().length === 0 && this.getPlayerPlanets().length === 0) {
      console.log('[MMOState] No player entities found, spawning starter entities');
      this.spawnStarterEntities();
    }
  }

  /**
   * Consume pending offline recap (returns once, then clears)
   */
  public consumeOfflineRecap(): OfflineRecapEvent[] | null {
    const recap = this.pendingOfflineRecap;
    this.pendingOfflineRecap = null;
    return recap;
  }

  /**
   * Update or add a chunk
   */
  public updateChunk(chunk: ChunkData): void {
    const chunkKey = `${chunk.chunkX},${chunk.chunkY}`;
    this.chunks.set(chunkKey, chunk);

    // Track which entities belong to this chunk
    const entityIds = this.chunkEntities.get(chunkKey) ?? new Set<string>();

    // Index all entities from this chunk
    chunk.units.forEach((unit) => {
      entityIds.add(unit.id);
      if (this.units.has(unit.id)) {
        // Unit already known — tick updates are authoritative for position,
        // so skip chunk data to avoid overwriting with stale cached positions.
        return;
      }
      this.units.set(unit.id, unit);
      this.indexEntity(unit.id, unit.position);
    });

    chunk.planets.forEach((planet) => {
      entityIds.add(planet.id);
      this.planets.set(planet.id, planet);
      this.indexEntity(planet.id, planet.position);
    });

    chunk.asteroids.forEach((asteroid) => {
      entityIds.add(asteroid.id);
      if (this.asteroids.has(asteroid.id)) {
        // Asteroid already known — tick updates are authoritative for moving asteroids.
        return;
      }
      this.asteroids.set(asteroid.id, asteroid);
      this.indexEntity(asteroid.id, asteroid.position);
    });

    if (chunk.stations) {
      chunk.stations.forEach((station) => {
        entityIds.add(station.id);
        this.stations.set(station.id, station);
        this.indexEntity(station.id, station.position);
      });
    }

    if (chunk.wrecks) {
      chunk.wrecks.forEach((wreck) => {
        entityIds.add(wreck.id);
        this.wrecks.set(wreck.id, wreck);
        this.indexEntity(wreck.id, wreck.position);
      });
    }

    if (chunk.ambientEntities) {
      chunk.ambientEntities.forEach((ae) => {
        entityIds.add(ae.id);
        if (this.ambientEntities.has(ae.id)) {
          // Already known — tick updates are authoritative for moving entities.
          return;
        }
        this.ambientEntities.set(ae.id, ae);
        this.indexEntity(ae.id, ae.position);
      });
    }

    // Process loot drops from chunk data
    if ((chunk as any).lootDrops) {
      for (const loot of (chunk as any).lootDrops) {
        entityIds.add(loot.id);
        this.lootDrops.set(loot.id, loot);
        this.indexEntity(loot.id, loot.position);
      }
    }

    // Process platforms from chunk data
    if ((chunk as any).platforms) {
      for (const platform of (chunk as any).platforms) {
        this.updatePlatform(platform);
      }
    }

    this.chunkEntities.set(chunkKey, entityIds);

    console.log(
      `[MMOState] Chunk (${chunk.chunkX},${chunk.chunkY}) loaded: ${chunk.units.length} units, ${chunk.planets.length} planets, ${chunk.asteroids.length} asteroids, ${chunk.stations?.length || 0} stations`,
    );
  }

  /**
   * Unload a chunk and remove all entities that belong to it
   */
  public unloadChunk(chunkX: number, chunkY: number): void {
    const chunkKey = `${chunkX},${chunkY}`;
    const entityIds = this.chunkEntities.get(chunkKey);

    if (entityIds) {
      for (const id of entityIds) {
        // Remove from entity maps and spatial index
        const unit = this.units.get(id);
        if (unit) {
          this.unindexEntity(id, unit.position);
          this.units.delete(id);
          continue;
        }
        const planet = this.planets.get(id);
        if (planet) {
          this.unindexEntity(id, planet.position);
          this.planets.delete(id);
          continue;
        }
        const asteroid = this.asteroids.get(id);
        if (asteroid) {
          this.unindexEntity(id, asteroid.position);
          this.asteroids.delete(id);
          continue;
        }
        const station = this.stations.get(id);
        if (station) {
          this.unindexEntity(id, station.position);
          this.stations.delete(id);
          continue;
        }
        const wreck = this.wrecks.get(id);
        if (wreck) {
          this.unindexEntity(id, wreck.position);
          this.wrecks.delete(id);
          continue;
        }
        const ambient = this.ambientEntities.get(id);
        if (ambient) {
          this.unindexEntity(id, ambient.position);
          this.ambientEntities.delete(id);
        }
      }
      this.chunkEntities.delete(chunkKey);
    }

    this.chunks.delete(chunkKey);
  }

  /**
   * Index an entity at a hex position for fast lookup
   */
  private indexEntity(entityId: string, position: HexCoord): void {
    const hexKey = this.hexToKey(position);

    if (!this.entitiesAtHex.has(hexKey)) {
      this.entitiesAtHex.set(hexKey, []);
    }

    const entities = this.entitiesAtHex.get(hexKey)!;
    if (!entities.includes(entityId)) {
      entities.push(entityId);
    }
  }

  /**
   * Remove entity from spatial index
   */
  private unindexEntity(entityId: string, position: HexCoord): void {
    const hexKey = this.hexToKey(position);
    const entities = this.entitiesAtHex.get(hexKey);

    if (entities) {
      const index = entities.indexOf(entityId);
      if (index > -1) {
        entities.splice(index, 1);
      }

      if (entities.length === 0) {
        this.entitiesAtHex.delete(hexKey);
      }
    }
  }

  /**
   * Get all entities at a specific hex
   */
  public getEntitiesAt(hex: HexCoord): (MMOUnit | MMOPlanet | MMOAsteroid | MMOStationData | MMOWreckData | MMOAmbientEntityData | MMOLootDropData)[] {
    const hexKey = this.hexToKey(hex);
    const entityIds = this.entitiesAtHex.get(hexKey) || [];

    return entityIds
      .map((id) => {
        return this.units.get(id) || this.planets.get(id) || this.asteroids.get(id) || this.stations.get(id) || this.wrecks.get(id) || this.ambientEntities.get(id) || this.lootDrops.get(id);
      })
      .filter((entity) => entity !== undefined) as (MMOUnit | MMOPlanet | MMOAsteroid | MMOStationData | MMOWreckData | MMOAmbientEntityData | MMOLootDropData)[];
  }

  /**
   * Get unit by ID
   */
  public getUnit(id: string): MMOUnit | undefined {
    return this.units.get(id);
  }

  /**
   * Get planet by ID
   */
  public getPlanet(id: string): MMOPlanet | undefined {
    return this.planets.get(id);
  }

  /**
   * Get asteroid by ID
   */
  public getAsteroid(id: string): MMOAsteroid | undefined {
    return this.asteroids.get(id);
  }

  /**
   * Get all asteroids
   */
  public getAllAsteroids(): MMOAsteroid[] {
    return Array.from(this.asteroids.values());
  }

  /**
   * Update or add a planet (from server tick update)
   */
  public updatePlanet(planetData: MMOPlanet): void {
    const existing = this.planets.get(planetData.id);

    if (existing) {
      // If position changed, reindex
      if (planetData.position.q !== existing.position.q || planetData.position.r !== existing.position.r) {
        this.unindexEntity(existing.id, existing.position);
        this.indexEntity(existing.id, planetData.position);
      }
      Object.assign(existing, planetData);
    } else {
      // New planet discovered
      this.planets.set(planetData.id, planetData);
      this.indexEntity(planetData.id, planetData.position);
    }
  }

  /**
   * Update an asteroid (from server tick update or mine result)
   */
  public updateAsteroid(asteroidData: Partial<MMOAsteroid> & { id: string }): void {
    const asteroid = this.asteroids.get(asteroidData.id);

    if (asteroid) {
      // If position changed, reindex
      if (
        asteroidData.position &&
        (asteroidData.position.q !== asteroid.position.q || asteroidData.position.r !== asteroid.position.r)
      ) {
        this.unindexEntity(asteroid.id, asteroid.position);
        this.indexEntity(asteroid.id, asteroidData.position);
      }

      // Preserve maxStorage — it represents initial ore total and must never decrease
      const prevMaxStorage = asteroid.maxStorage;
      Object.assign(asteroid, asteroidData);
      if (prevMaxStorage > 0 && asteroid.maxStorage < prevMaxStorage) {
        asteroid.maxStorage = prevMaxStorage;
      }
    }
  }

  /**
   * Check if a unit is docked
   */
  public isUnitDocked(unitId: string): boolean {
    const unit = this.getUnit(unitId);
    return !!unit?.dockedAtPlanetId;
  }

  /**
   * Get all units docked at a specific planet
   */
  public getDockedUnitsAtPlanet(planetId: string): MMOUnit[] {
    return Array.from(this.units.values()).filter((u) => u.dockedAtPlanetId === planetId);
  }

  /**
   * Update or add a unit (from server tick update)
   */
  public updateUnit(unitData: Partial<MMOUnit> & { id: string }): void {
    const unit = this.units.get(unitData.id);

    if (unit) {
      // If position changed, reindex
      if (unitData.position && (unitData.position.q !== unit.position.q || unitData.position.r !== unit.position.r)) {
        this.unindexEntity(unit.id, unit.position);
        this.indexEntity(unit.id, unitData.position);
      }

      // Update properties
      Object.assign(unit, unitData);
    } else if (unitData.position) {
      // New unit discovered (e.g. just built or entered fog of war)
      // Build defaults, then overlay with whatever the server sent
      const d = unitData as any;
      const defaults: MMOUnit = {
        id: unitData.id,
        type: d.type || d.unitType || 'Unknown',
        ownerId: d.ownerId || '',
        position: unitData.position,
        currentHp: d.currentHp ?? d.current_hp ?? 100,
        maxHp: d.maxHp ?? d.max_hp ?? 100,
        shields: d.shields ?? 0,
        maxShields: d.maxShields ?? d.max_shields ?? 0,
        movementOrder: d.movementOrder ?? null,
        speed: d.speed ?? d.base_speed ?? d.moveRange ?? d.move_range ?? 1,
        attackRange: d.attackRange ?? d.attack_range ?? 1,
        attack: d.attack ?? 10,
        defense: d.defense ?? 10,
        dockedAtPlanetId: d.dockedAtPlanetId ?? null,
        cargoCapacity: d.cargoCapacity ?? 100,
        cargoUsed: d.cargoUsed ?? 0,
        miningAsteroidId: d.miningAsteroidId ?? null,
        miningProgress: d.miningProgress ?? 0,
        activeEffects: d.activeEffects ?? [],
        abilities: d.abilities ?? [],
      };
      // Overlay server data on top of defaults
      Object.assign(defaults, unitData);
      this.units.set(defaults.id, defaults);
      this.indexEntity(defaults.id, defaults.position);
    }
  }

  /**
   * Remove a unit (destroyed)
   */
  public removeUnit(unitId: string): void {
    const unit = this.units.get(unitId);
    if (unit) {
      this.unindexEntity(unitId, unit.position);
      this.units.delete(unitId);
    }
  }

  /**
   * Remove an asteroid (delivered or destroyed)
   */
  public addAsteroid(asteroid: MMOAsteroid): void {
    if (this.asteroids.has(asteroid.id)) return;
    this.asteroids.set(asteroid.id, asteroid);
    this.indexEntity(asteroid.id, asteroid.position);
  }

  public removeAsteroid(asteroidId: string): void {
    const asteroid = this.asteroids.get(asteroidId);
    if (asteroid) {
      this.unindexEntity(asteroidId, asteroid.position);
      this.asteroids.delete(asteroidId);
    }
  }

  /**
   * Get station by ID
   */
  public getStation(id: string): MMOStationData | undefined {
    return this.stations.get(id);
  }

  /**
   * Add or update a wreck (e.g. from tick broadcast)
   */
  public addWreck(wreck: MMOWreckData): void {
    const existing = this.wrecks.get(wreck.id);
    if (existing) {
      this.unindexEntity(wreck.id, existing.position);
    }
    this.wrecks.set(wreck.id, wreck);
    this.indexEntity(wreck.id, wreck.position);
  }

  /**
   * Get wreck by ID
   */
  public getWreck(id: string): MMOWreckData | undefined {
    return this.wrecks.get(id);
  }

  /**
   * Remove a wreck
   */
  public removeWreck(wreckId: string): void {
    const wreck = this.wrecks.get(wreckId);
    if (wreck) {
      this.unindexEntity(wreckId, wreck.position);
      this.wrecks.delete(wreckId);
    }
  }

  /**
   * Add or update an ambient entity
   */
  public updateAmbientEntity(data: MMOAmbientEntityData): void {
    const existing = this.ambientEntities.get(data.id);
    if (existing) {
      if (data.position.q !== existing.position.q || data.position.r !== existing.position.r) {
        this.unindexEntity(existing.id, existing.position);
        this.indexEntity(existing.id, data.position);
      }
      Object.assign(existing, data);
    } else {
      this.ambientEntities.set(data.id, data);
      this.indexEntity(data.id, data.position);
    }
  }

  /**
   * Remove an ambient entity
   */
  public removeAmbientEntity(entityId: string): void {
    const entity = this.ambientEntities.get(entityId);
    if (entity) {
      this.unindexEntity(entityId, entity.position);
      this.ambientEntities.delete(entityId);
    }
  }

  /**
   * Add or update a loot drop
   */
  public updateLootDrop(data: MMOLootDropData): void {
    const existing = this.lootDrops.get(data.id);
    if (existing) {
      if (data.position.q !== existing.position.q || data.position.r !== existing.position.r) {
        this.unindexEntity(existing.id, existing.position);
        this.indexEntity(existing.id, data.position);
      }
      Object.assign(existing, data);
    } else {
      this.lootDrops.set(data.id, data);
      this.indexEntity(data.id, data.position);
    }
  }

  /**
   * Remove a loot drop (claimed or expired)
   */
  public removeLootDrop(lootId: string): void {
    const loot = this.lootDrops.get(lootId);
    if (loot) {
      this.unindexEntity(lootId, loot.position);
      this.lootDrops.delete(lootId);
    }
  }

  /**
   * Get a loot drop by ID
   */
  public getLootDrop(id: string): MMOLootDropData | undefined {
    return this.lootDrops.get(id);
  }

  /**
   * Get all wrecks
   */
  public getAllWrecks(): MMOWreckData[] {
    return Array.from(this.wrecks.values());
  }

  /**
   * Get all planets
   */
  public getAllPlanets(): MMOPlanet[] {
    return Array.from(this.planets.values());
  }

  /**
   * Get all stations
   */
  public getAllStations(): MMOStationData[] {
    return Array.from(this.stations.values());
  }

  /**
   * Get all stations owned by player
   */
  public getPlayerStations(): MMOStationData[] {
    if (!this.userId) return [];
    return Array.from(this.stations.values()).filter((station) => station.userId === this.userId);
  }

  /**
   * Update or add a station (from server tick update)
   */
  public updateStation(stationData: MMOStationData): void {
    const existing = this.stations.get(stationData.id);

    if (existing) {
      if (stationData.position.q !== existing.position.q || stationData.position.r !== existing.position.r) {
        this.unindexEntity(existing.id, existing.position);
        this.indexEntity(existing.id, stationData.position);
      }
      Object.assign(existing, stationData);
    } else {
      this.stations.set(stationData.id, stationData);
      this.indexEntity(stationData.id, stationData.position);
    }
  }

  /**
   * Remove a station
   */
  public removeStation(stationId: string): void {
    const station = this.stations.get(stationId);
    if (station) {
      this.unindexEntity(stationId, station.position);
      this.stations.delete(stationId);
    }
  }

  // ── Orbital Platforms ────────────────────────────────────────

  /**
   * Update or add an orbital platform
   */
  updatePlatform(data: OrbitalPlatformData): void {
    const existing = this.platforms.get(data.id);
    if (existing) {
      Object.assign(existing, data);
    } else {
      this.platforms.set(data.id, { ...data });
    }
  }

  /**
   * Remove an orbital platform
   */
  removePlatform(id: string): void {
    this.platforms.delete(id);
  }

  /**
   * Get an orbital platform by ID
   */
  getPlatform(id: string): OrbitalPlatformData | undefined {
    return this.platforms.get(id);
  }

  /**
   * Get all orbital platforms
   */
  getAllPlatforms(): OrbitalPlatformData[] {
    return Array.from(this.platforms.values());
  }

  /**
   * Get all orbital platforms for a specific planet
   */
  getPlatformsForPlanet(planetId: string): OrbitalPlatformData[] {
    return Array.from(this.platforms.values()).filter(p => p.planetId === planetId);
  }

  /**
   * Check if hex is explored (ever seen)
   */
  public isHexExplored(hexKey: string): boolean {
    return this.fogOfWar.has(hexKey);
  }

  /**
   * Check if hex is currently visible
   */
  public isHexVisible(hexKey: string): boolean {
    return this.currentVision.has(hexKey);
  }

  /**
   * Add a hex to explored fog of war
   */
  public addExploredHex(hexKey: string): void {
    this.fogOfWar.add(hexKey);
    this.currentVision.add(hexKey);
  }

  /**
   * Update current vision (from server tick update)
   */
  public updateVision(explored: string[], visible: string[]): void {
    // Add new explored hexes
    explored.forEach((hexKey) => this.fogOfWar.add(hexKey));

    // Replace current vision
    this.currentVision.clear();
    visible.forEach((hexKey) => this.currentVision.add(hexKey));
  }

  /**
   * Set player alliance data
   */
  public setAlliance(alliance: AllianceData | null): void {
    this.playerAlliance = alliance;
  }

  /**
   * Get player alliance
   */
  public getAlliance(): AllianceData | null {
    return this.playerAlliance;
  }

  /**
   * Set alliance territories for visualization
   */
  public setTerritories(data: TerritoryData[]): void {
    this.territories.clear();
    for (const t of data) {
      this.territories.set(t.allianceId, t);
    }
  }

  /**
   * Get all alliance territories
   */
  public getTerritories(): TerritoryData[] {
    return Array.from(this.territories.values());
  }

  /**
   * Set player resources
   */
  public setResources(amount: number): void {
    this.playerResources = amount;
  }

  /**
   * Get player resources
   */
  public getResources(): number {
    return this.playerResources;
  }

  // ── Build queue ──────────────────────────────────────────────

  /**
   * Add or update a pending build
   */
  public updatePendingBuild(data: {
    buildId: string;
    planetId: string;
    unitType: string;
    remainingSeconds: number;
    totalSeconds: number;
  }): void {
    this.pendingBuilds.set(data.buildId, data);
  }

  /**
   * Remove a completed or cancelled build
   */
  public removePendingBuild(buildId: string): void {
    this.pendingBuilds.delete(buildId);
  }

  /**
   * Get all pending builds (optionally filtered by planetId)
   */
  public getPendingBuilds(planetId?: string): Array<{
    buildId: string;
    planetId: string;
    unitType: string;
    remainingSeconds: number;
    totalSeconds: number;
  }> {
    const all = Array.from(this.pendingBuilds.values());
    if (planetId) return all.filter(b => b.planetId === planetId);
    return all;
  }

  /**
   * Set current tick number
   */
  public setTick(tick: number): void {
    this.tickNumber = tick;
  }

  /**
   * Get current tick number
   */
  public getTick(): number {
    return this.tickNumber;
  }

  /**
   * Rebuild the entire spatial index from scratch.
   * Fixes any desync between entity positions and the entitiesAtHex map.
   */
  public rebuildSpatialIndex(): void {
    this.entitiesAtHex.clear();

    for (const unit of this.units.values()) {
      this.indexEntity(unit.id, unit.position);
    }
    for (const planet of this.planets.values()) {
      this.indexEntity(planet.id, planet.position);
    }
    for (const asteroid of this.asteroids.values()) {
      this.indexEntity(asteroid.id, asteroid.position);
    }
    for (const station of this.stations.values()) {
      this.indexEntity(station.id, station.position);
    }
    for (const wreck of this.wrecks.values()) {
      this.indexEntity(wreck.id, wreck.position);
    }
    for (const ambient of this.ambientEntities.values()) {
      this.indexEntity(ambient.id, ambient.position);
    }
    for (const loot of this.lootDrops.values()) {
      this.indexEntity(loot.id, loot.position);
    }
  }

  /**
   * Get all units owned by player
   */
  public getPlayerUnits(): MMOUnit[] {
    if (!this.userId) return [];
    return Array.from(this.units.values()).filter((unit) => unit.ownerId === this.userId);
  }

  /**
   * Get all known units (all owners, visible in loaded chunks)
   */
  public getAllUnits(): MMOUnit[] {
    return Array.from(this.units.values());
  }

  public getAllAmbientEntities(): MMOAmbientEntityData[] {
    return Array.from(this.ambientEntities.values());
  }

  public getAllLootDrops(): MMOLootDropData[] {
    return Array.from(this.lootDrops.values());
  }

  /**
   * Get all planets owned by player
   */
  public getPlayerPlanets(): MMOPlanet[] {
    if (!this.userId) return [];
    return Array.from(this.planets.values()).filter((planet) => planet.ownerId === this.userId);
  }

  /**
   * Get world ID
   */
  public getWorldId(): number {
    return this.worldId;
  }

  /**
   * Get user ID
   */
  public getUserId(): string | null {
    return this.userId;
  }

  /**
   * Get player's starting position (home planet)
   */
  public getPlayerPosition(): HexCoord | null {
    if (!this.userId) return null;

    // Find player's home planet
    const homePlanet = Array.from(this.planets.values()).find(
      (planet) => planet.ownerId === this.userId && planet.isHomePlanet,
    );

    return homePlanet ? homePlanet.position : null;
  }

  /**
   * Get all explored hexes
   */
  public getExploredHexes(): Set<string> {
    return new Set(this.fogOfWar);
  }

  /**
   * Get all visible hexes
   */
  public getVisibleHexes(): Set<string> {
    return new Set(this.currentVision);
  }

  /**
   * Convert hex coordinate to key string
   */
  private hexToKey(hex: HexCoord): string {
    return `${hex.q},${hex.r}`;
  }

  /**
   * Convert key string to hex coordinate
   */
  public keyToHex(key: string): HexCoord {
    const [q, r] = key.split(',').map(Number);
    return { q, r };
  }

  /**
   * Spawn starter entities for new player
   */
  private spawnStarterEntities(): void {
    if (!this.userId) return;

    const earthPosition: HexCoord = { q: 0, r: 0 };

    // Create Earth as shared planet (only if it doesn't exist)
    if (!this.planets.has('planet_earth')) {
      const earth: MMOPlanet = {
        id: 'planet_earth',
        name: 'Earth',
        ownerId: null, // Shared planet - no owner
        position: earthPosition,
        currentHp: 10000,
        maxHp: 10000,
        shields: 5000,
        maxShields: 5000,
        buildings: [],
        productionQueue: [],
        isHomePlanet: false,
      };

      this.planets.set(earth.id, earth);
      this.indexEntity(earth.id, earth.position);
      console.log('[MMOState] Created shared Earth planet at (0,0)');
    }

    // Create starter units around home planet
    const starterUnits: MMOUnit[] = [
      {
        id: `unit_scout_${this.userId}_1`,
        type: 'Scout',
        ownerId: this.userId,
        position: { q: 1, r: 0 },
        currentHp: 50,
        maxHp: 50,
        shields: 25,
        maxShields: 25,
        movementOrder: null,
        speed: 5,
        attackRange: 1,
        attack: 5,
        defense: 0,
      },
      {
        id: `unit_fighter_${this.userId}_1`,
        type: 'Fighter',
        ownerId: this.userId,
        position: { q: 0, r: 1 },
        currentHp: 100,
        maxHp: 100,
        shields: 50,
        maxShields: 50,
        movementOrder: null,
        speed: 3,
        attackRange: 1,
        attack: 15,
        defense: 0,
      },
      {
        id: `unit_colony_${this.userId}_1`,
        type: 'Colony Ship',
        ownerId: this.userId,
        position: { q: -1, r: 1 },
        currentHp: 75,
        maxHp: 75,
        shields: 30,
        maxShields: 30,
        movementOrder: null,
        speed: 2,
        attackRange: 0,
        attack: 0,
        defense: 0,
      },
    ];

    starterUnits.forEach((unit) => {
      this.units.set(unit.id, unit);
      this.indexEntity(unit.id, unit.position);
    });

    // Set up initial fog of war (7 hex radius around home)
    const visionRadius = 7;
    for (let q = -visionRadius; q <= visionRadius; q++) {
      for (let r = -visionRadius; r <= visionRadius; r++) {
        const hexKey = `${q},${r}`;
        // Only add hexes within actual radius (hex distance)
        if (Math.abs(q) + Math.abs(r) + Math.abs(-q - r) <= visionRadius * 2) {
          this.fogOfWar.add(hexKey);
          this.currentVision.add(hexKey);
        }
      }
    }

    console.log(
      `[MMOState] Spawned starter entities for player ${this.userId}: ${starterUnits.length} units at Earth, ${this.fogOfWar.size} explored hexes`,
    );
  }
}
